import React, { useState, useCallback } from "react";
import { MenuItem, ListItemIcon, ListItemText, Menu } from "@mui/material";
import {
  ApartmentOutlined,
  SettingsOutlined,
  Inventory2Outlined,
  ViewModuleOutlined,
  EditNoteOutlined,
  ChevronRight,
} from "@mui/icons-material";
import FamilyManagement from "./menus/FamilyManagementProduct/FamilyManagement";
import { InventoryCombinationsDisplay } from "./menus/ProductInventoryCombinations/InventoryCombinationsDisplay";
import { CreateProductWizard } from "../../../../components/forms/CreateProductWizard/CreateProductWizard";
import {
  BaseContextMenu,
  BaseContextMenuProps,
} from "../../../../components/datagrid/components/BaseContextMenu";
import { title } from "process";

const ListingContextMenu: React.FC<BaseContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh,
  ...rest
}) => {
  const [inventoryAnchorEl, setInventoryAnchorEl] =
    useState<null | HTMLElement>(null);
  const [activeModal, setActiveModal] = useState<string>("");
  const [productWizardConfig, setProductWizardConfig] = useState<{
    initialProduct: any;
    mode: "edit" | "create";
    initialPage: number;
  } | null>(null);

  const productWizardProductTransform = (row: any) => {
    return {
      product_id: row.product.product_id,
      families:
        row.product_families.map((f: any) => ({
          title: f.label,
          id: f.value,
        })) || [],
      title: row.product.product_title || "",
      asin: row.product.product_asin || "",
      partNumber: row.product.assembly_part_number || "",
    };
  };

  const openContextModal = useCallback((modalName: string) => {
    setActiveModal(modalName);
    setInventoryAnchorEl(null);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
  }, []);

  const closeModalAndRefresh = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
    if (onGridRefresh) {
      onGridRefresh();
    }
    closeContextMenu();
  }, [onGridRefresh]);

  const handleOpenEditPartNumber = useCallback(() => {
    if (selectedRows.length === 0) return;

    const row = selectedRows[0];
    setProductWizardConfig({
      initialProduct: productWizardProductTransform(row),
      mode: "edit",
      initialPage: 1,
    });
    setActiveModal("productWizard");
    setInventoryAnchorEl(null);
    closeContextMenu();
  }, [selectedRows, closeContextMenu]);

  const handleOpenEditInventory = useCallback(() => {
    if (selectedRows.length === 0) return;

    const row = selectedRows[0];
    setProductWizardConfig({
      initialProduct: productWizardProductTransform(row),
      mode: "edit",
      initialPage: 2, // Start on inventory page
    });
    setActiveModal("productWizard");
    setInventoryAnchorEl(null);
    closeContextMenu();
  }, [selectedRows, closeContextMenu]);

  const handleInventoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInventoryAnchorEl(event.currentTarget);
  };

  const handleInventoryMenuClose = () => {
    setInventoryAnchorEl(null);
  };

  const handleMainMenuClose = () => {
    setInventoryAnchorEl(null);
    setActiveModal("");
    setProductWizardConfig(null);
    closeContextMenu();
  };

  // Resolve which custom modal to show (or null for none)
  let resolvedCustomModal: React.ReactNode | null = null;
  if (activeModal) {
    switch (activeModal) {
      case "family":
        resolvedCustomModal = (
          <FamilyManagement
            open={true}
            close={closeModalAndRefresh}
            initialProducts={selectedRows}
          />
        );
        break;
      case "inventory-combinations":
        if (selectedRows.length === 1) {
          resolvedCustomModal = (
            <InventoryCombinationsDisplay
              open={true}
              close={closeModal}
              product={selectedRows[0]}
            />
          );
        }
        break;
      case "productWizard":
        if (productWizardConfig) {
          resolvedCustomModal = (
            <CreateProductWizard
              mode={productWizardConfig.mode}
              initialProduct={productWizardConfig.initialProduct}
              initialPage={productWizardConfig.initialPage}
              onClose={closeModalAndRefresh}
            />
          );
        }
        break;
    }
  }

  const inventorySubmenu = (
    <Menu
      open={Boolean(inventoryAnchorEl)}
      anchorEl={inventoryAnchorEl}
      onClose={handleInventoryMenuClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        root: {
          onMouseLeave: handleInventoryMenuClose,
        },
      }}
    >
      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={() => openContextModal("inventory-combinations")}
      >
        <ListItemIcon>
          <ViewModuleOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Sets</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={handleOpenEditInventory}
      >
        <ListItemIcon>
          <EditNoteOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Listing</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <BaseContextMenu
      open={open}
      event={event}
      closeContextMenu={handleMainMenuClose}
      selectedRows={selectedRows}
      onGridRefresh={onGridRefresh}
      activeCustomModal={resolvedCustomModal}
      extraNodes={inventorySubmenu}
      {...rest}
    >
      <MenuItem onClick={() => openContextModal("family")}>
        <ListItemIcon>
          <ApartmentOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Families</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={handleOpenEditPartNumber}
      >
        <ListItemIcon>
          <SettingsOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Part Number</ListItemText>
      </MenuItem>

      <MenuItem onMouseEnter={handleInventoryMenuOpen}>
        <ListItemIcon>
          <Inventory2Outlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Inventory</ListItemText>
        <ChevronRight fontSize="small" />
      </MenuItem>
    </BaseContextMenu>
  );
};

export default ListingContextMenu;
